import { achievementDefinitions } from "@/features/achievements/achievementDefinitions";
import { normalizeAchievementId } from "@/features/achievements/achievementIdAliases";
import type {
  AchievementId,
  AchievementsState,
} from "@/features/achievements/types";
import type { HoverIdleModelState } from "@/features/hoverIdle/types";
import {
  EXPLORATION_BIOME_IDS,
  type ExplorationBiomeId,
  type ExplorationMpSlotId,
} from "@/features/hoverIdle/exploration/explorationTypes";
import { isExplorationMpSlotId } from "@/features/hoverIdle/exploration/explorationDefinitions";
import { createDefaultMpmState } from "@/features/hoverIdle/mpm/mpmState";
import {
  isHovSkillId,
  type HovSkillId,
} from "@/features/hoverIdle/hov/hovSkillDefinitions";
import type { HovSkillState } from "@/features/hoverIdle/hov/hovSkillModel";
import {
  isNonNegativeHuge,
  parseHugeInput,
  type HugeNumber,
} from "@/shared/numbers";

export interface HoverIdleE2EInitialStatePatch {
  readonly settings?: {
    readonly firstAccessTutorialCompleted?: boolean;
  };
  readonly economy?: {
    readonly hov?: string;
    readonly tim?: string;
    readonly fai?: number;
  };
  readonly oHov?: {
    readonly count?: number;
    readonly power?: string;
    readonly hovCore?: string;
  };
  readonly exploration?: {
    readonly biomeLevel?: number;
    readonly purchasedMpSlotCount?: number;
    readonly areas?: Readonly<
      Partial<
        Record<
          ExplorationBiomeId,
          {
            readonly progressMs?: number;
          }
        >
      >
    >;
    readonly assignedSlotIds?: readonly ExplorationMpSlotId[];
  };
  readonly hovSkills?: HovSkillState;
  readonly achievements?: AchievementsState;
}

const ACHIEVEMENT_IDS = new Set<AchievementId>(
  achievementDefinitions.map((definition) => definition.id),
);

declare global {
  interface Window {
    __HOVER_IDLE_E2E_STATE__?: unknown;
  }
}

export function readE2EInitialStatePatch(): HoverIdleE2EInitialStatePatch | null {
  if (!isE2EInitialStateHookEnabled()) {
    return null;
  }
  if (typeof window === "undefined") {
    return null;
  }

  const raw = window.__HOVER_IDLE_E2E_STATE__;
  if (!isObject(raw)) {
    return null;
  }

  const settings = readSettingsPatch(raw.settings);
  const economy = readEconomyPatch(raw.economy);
  const oHov = readOHovPatch(raw.oHov);
  const exploration = readExplorationPatch(raw.exploration);
  const hovSkills = readHovSkillStatePatch(raw.hovSkills);
  const achievements = readAchievementsPatch(raw.achievements);

  if (
    settings === null &&
    economy === null &&
    oHov === null &&
    exploration === null &&
    hovSkills === null &&
    achievements === null
  ) {
    return null;
  }

  return {
    ...(settings === null ? {} : { settings }),
    ...(economy === null ? {} : { economy }),
    ...(oHov === null ? {} : { oHov }),
    ...(exploration === null ? {} : { exploration }),
    ...(hovSkills === null ? {} : { hovSkills }),
    ...(achievements === null ? {} : { achievements }),
  };
}

export function applyE2EInitialStatePatch(
  state: HoverIdleModelState,
  patch: HoverIdleE2EInitialStatePatch | null,
): HoverIdleModelState {
  if (patch === null) {
    return state;
  }

  let next = state;

  if (patch.settings?.firstAccessTutorialCompleted !== undefined) {
    next = {
      ...next,
      settings: {
        ...next.settings,
        firstAccessTutorialCompleted:
          patch.settings.firstAccessTutorialCompleted,
      },
    };
  }

  let nextEconomy = next.economy;
  const hov = parseNonNegativeHugeString(patch.economy?.hov);
  if (hov !== null) {
    nextEconomy = { ...nextEconomy, hov };
  }

  const tim = parseNonNegativeHugeString(patch.economy?.tim);
  if (tim !== null) {
    nextEconomy = { ...nextEconomy, tim };
  }

  const fai = patch.economy?.fai;
  if (isFiniteNonNegativeNumber(fai)) {
    nextEconomy = { ...nextEconomy, fai };
  }

  const oHovPatch = patch.oHov;
  if (oHovPatch !== undefined) {
    let nextOHov = nextEconomy.oHov;

    if (isNonNegativeInteger(oHovPatch.count)) {
      nextOHov = { ...nextOHov, count: oHovPatch.count };
    }

    const power = parseNonNegativeHugeString(oHovPatch.power);
    if (power !== null) {
      nextOHov = { ...nextOHov, power };
    }

    const hovCore = parseNonNegativeHugeString(oHovPatch.hovCore);
    if (hovCore !== null) {
      nextOHov = { ...nextOHov, hovCore };
    }

    if (nextOHov !== nextEconomy.oHov) {
      nextEconomy = { ...nextEconomy, oHov: nextOHov };
    }
  }

  const explorationPatch = patch.exploration;
  if (explorationPatch !== undefined) {
    const currentExploration = nextEconomy.exploration;
    if (currentExploration !== undefined) {
      let areas = currentExploration.areas;
      for (const biomeId of EXPLORATION_BIOME_IDS) {
        const areaPatch = explorationPatch.areas?.[biomeId];
        if (areaPatch === undefined) continue;
        areas = {
          ...areas,
          [biomeId]: {
            ...areas[biomeId],
            ...(areaPatch.progressMs === undefined
              ? {}
              : { progressMs: areaPatch.progressMs }),
          },
        };
      }
      nextEconomy = {
        ...nextEconomy,
        exploration: {
          ...currentExploration,
          ...(explorationPatch.biomeLevel === undefined
            ? {}
            : { biomeLevel: explorationPatch.biomeLevel }),
          ...(explorationPatch.purchasedMpSlotCount === undefined
            ? {}
            : {
                purchasedMpSlotCount: explorationPatch.purchasedMpSlotCount,
              }),
          areas,
        },
      };
    }

    if (explorationPatch.assignedSlotIds !== undefined) {
      const mpm = nextEconomy.mpm ?? createDefaultMpmState();
      nextEconomy = {
        ...nextEconomy,
        mpm: {
          ...mpm,
          exploration: {
            ...mpm.exploration,
            assignedSlotIds: explorationPatch.assignedSlotIds,
          },
        },
      };
    }
  }

  if (nextEconomy !== next.economy) {
    next = { ...next, economy: nextEconomy };
  }

  return next;
}

function isE2EInitialStateHookEnabled(): boolean {
  return (
    import.meta.env.DEV === true ||
    import.meta.env.VITE_ENABLE_E2E_TEST_HOOKS === "1"
  );
}

function readSettingsPatch(
  raw: unknown,
): HoverIdleE2EInitialStatePatch["settings"] | null {
  if (!isObject(raw)) {
    return null;
  }
  if (typeof raw.firstAccessTutorialCompleted !== "boolean") {
    return null;
  }
  return {
    firstAccessTutorialCompleted: raw.firstAccessTutorialCompleted,
  };
}

function readEconomyPatch(
  raw: unknown,
): HoverIdleE2EInitialStatePatch["economy"] | null {
  if (!isObject(raw)) {
    return null;
  }

  const patch: {
    hov?: string;
    tim?: string;
    fai?: number;
  } = {};

  if (typeof raw.hov === "string") {
    patch.hov = raw.hov;
  }
  if (typeof raw.tim === "string") {
    patch.tim = raw.tim;
  }
  if (isFiniteNonNegativeNumber(raw.fai)) {
    patch.fai = raw.fai;
  }

  return hasOwnKeys(patch) ? patch : null;
}

function readOHovPatch(
  raw: unknown,
): HoverIdleE2EInitialStatePatch["oHov"] | null {
  if (!isObject(raw)) {
    return null;
  }

  const patch: {
    count?: number;
    power?: string;
    hovCore?: string;
  } = {};

  if (typeof raw.count === "number") {
    patch.count = raw.count;
  }
  if (typeof raw.power === "string") {
    patch.power = raw.power;
  }
  if (typeof raw.hovCore === "string") {
    patch.hovCore = raw.hovCore;
  }

  return hasOwnKeys(patch) ? patch : null;
}

function readExplorationPatch(
  raw: unknown,
): HoverIdleE2EInitialStatePatch["exploration"] | null {
  if (!isObject(raw)) return null;
  const patch: {
    biomeLevel?: number;
    purchasedMpSlotCount?: number;
    areas?: Partial<
      Record<
        ExplorationBiomeId,
        {
          progressMs?: number;
        }
      >
    >;
    assignedSlotIds?: ExplorationMpSlotId[];
  } = {};

  if (isPositiveInteger(raw.biomeLevel)) {
    patch.biomeLevel = raw.biomeLevel;
  }
  if (isNonNegativeInteger(raw.purchasedMpSlotCount)) {
    patch.purchasedMpSlotCount = raw.purchasedMpSlotCount;
  }

  if (isObject(raw.areas)) {
    const areas: NonNullable<typeof patch.areas> = {};
    for (const biomeId of EXPLORATION_BIOME_IDS) {
      const rawArea = raw.areas[biomeId];
      if (!isObject(rawArea)) continue;
      const area: {
        progressMs?: number;
      } = {};
      if (isFiniteNonNegativeNumber(rawArea.progressMs)) {
        area.progressMs = rawArea.progressMs;
      }
      if (hasOwnKeys(area)) {
        areas[biomeId] = area;
      }
    }
    if (hasOwnKeys(areas)) {
      patch.areas = areas;
    }
  }

  if (Array.isArray(raw.assignedSlotIds)) {
    const assignedSlotIds = raw.assignedSlotIds.filter(isExplorationMpSlotId);
    if (assignedSlotIds.length > 0) {
      patch.assignedSlotIds = assignedSlotIds;
    }
  }

  return hasOwnKeys(patch) ? patch : null;
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value > 0;
}

function readAchievementsPatch(raw: unknown): AchievementsState | null {
  if (!isObject(raw)) {
    return null;
  }

  const unlockedIds = readAchievementIds(raw.unlockedIds);
  const seenIds = readAchievementIds(raw.seenIds);
  if (unlockedIds === null && seenIds === null) {
    return null;
  }

  const unlocked = unlockedIds ?? [];
  const unlockedSet = new Set<AchievementId>(unlocked);
  return {
    unlockedIds: unlocked,
    seenIds: (seenIds ?? []).filter((id) => unlockedSet.has(id)),
  };
}

function readHovSkillStatePatch(raw: unknown): HovSkillState | null {
  if (!isObject(raw) || !Array.isArray(raw.acquiredIds)) {
    return null;
  }
  const acquiredIds: HovSkillId[] = [];
  for (const value of raw.acquiredIds) {
    if (typeof value === "string" && isHovSkillId(value)) {
      acquiredIds.push(value);
    }
  }
  return { acquiredIds: [...new Set(acquiredIds)] };
}

function readAchievementIds(raw: unknown): AchievementId[] | null {
  if (!Array.isArray(raw)) {
    return null;
  }

  const ids: AchievementId[] = [];
  for (const value of raw) {
    if (typeof value !== "string") {
      continue;
    }
    const id = normalizeAchievementId(value);
    if (id === null || !ACHIEVEMENT_IDS.has(id)) {
      continue;
    }
    ids.push(id);
  }
  return [...new Set(ids)];
}

function parseNonNegativeHugeString(
  value: string | undefined,
): HugeNumber | null {
  if (value === undefined) {
    return null;
  }

  const parsed = parseHugeInput(value);
  if (parsed === null || !isNonNegativeHuge(parsed)) {
    return null;
  }
  return parsed;
}

function isFiniteNonNegativeNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

function isObject(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasOwnKeys(value: object): boolean {
  return 0 < Object.keys(value).length;
}
